/**
 * Sawyer E4 (#754) — the ONE unread poller for Team messages (P1: one
 * source). The nav badge, the phone top-bar badge and the page's tab counts
 * all read this store, so a tab makes one request per tick however many
 * components show the number.
 *
 * Polite (AC15): every 30s, only while the tab is visible, only while at
 * least one ENABLED subscriber is mounted (enabled = the staffer's role
 * reaches Team messages AND the server has it, AC17). Hidden → paused;
 * visible again → refresh at once. `refreshUnread()` after a post, a send or
 * a mark-read.
 *
 * Silent on failure (MAC1): a 500, a timeout or a malformed body leaves the
 * last good count (or nothing) in place, logs nothing, and the next tick
 * retries.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { fetchUnreadQuiet, type UnreadCounts } from '../../lib/staff-messages-api.js';

export const UNREAD_POLL_MS = 30_000;

type Listener = () => void;

let counts: UnreadCounts | null = null;
let apiUrl: string | null = null;
let enabledSubscribers = 0;
let timer: ReturnType<typeof setInterval> | null = null;
let inFlight = false;
const listeners = new Set<Listener>();

function emit(): void {
  for (const l of listeners) l();
}

function setCounts(next: UnreadCounts): void {
  if (counts && counts.board === next.board && counts.direct === next.direct && counts.total === next.total) return;
  counts = next;
  emit();
}

function visible(): boolean {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

async function tick(): Promise<void> {
  if (!apiUrl || enabledSubscribers === 0 || !visible() || inFlight) return;
  inFlight = true;
  try {
    const next = await fetchUnreadQuiet(apiUrl);
    if (next) setCounts(next);
  } finally {
    inFlight = false;
  }
}

function onVisibility(): void {
  if (visible()) void tick();
}

function start(): void {
  if (timer !== null) return;
  timer = setInterval(() => void tick(), UNREAD_POLL_MS);
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
}

function stop(): void {
  if (timer !== null) clearInterval(timer);
  timer = null;
  if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
}

/** Seed from the availability probe's 200 body (review C1: one request on mount). */
export function seedUnread(next: UnreadCounts): void {
  setCounts(next);
}

/** Refresh now (after a post, a send or a mark-read). */
export function refreshUnread(): Promise<void> {
  return tick();
}

/** Test seam: forget everything between cases. */
export function __resetUnreadForTests(): void {
  stop();
  counts = null;
  apiUrl = null;
  enabledSubscribers = 0;
  inFlight = false;
  listeners.clear();
}

function subscribe(l: Listener): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

const snapshot = (): UnreadCounts | null => counts;

/**
 * Read the shared unread counts. Pass `enabled` false (role cannot reach
 * the surface, or the server does not have it) and nothing is fetched and
 * `null` comes back.
 */
export function useStaffMessagesUnread(url: string, enabled: boolean): UnreadCounts | null {
  const value = useSyncExternalStore(subscribe, snapshot, snapshot);
  useEffect(() => {
    if (!enabled) return undefined;
    apiUrl = url;
    enabledSubscribers += 1;
    // The probe usually seeded a value already; fetch only when it did not.
    if (counts === null) void tick();
    start();
    return () => {
      enabledSubscribers -= 1;
      if (enabledSubscribers <= 0) {
        enabledSubscribers = 0;
        stop();
      }
    };
  }, [url, enabled]);
  return enabled ? value : null;
}
